/**
 * Roles: **what a person does here**, as a set — admin, engineer, technician,
 * intake. A person holds any number of them, and at least one.
 *
 * They were five (§11.4: manager, designer, purchaser, builder, maintenance),
 * named after the steps of the production loop. Nobody at Nupeak describes
 * themselves that way, and the roster read as a quiz. These are the four jobs
 * people actually have, and the app looks different for each:
 *
 *  - **technician** — paid by the hour. Clocks in and out, says when they can
 *    come in, answers requests to, and does the building and maintaining.
 *    Holding the role *is* being hourly: there is no separate pay flag to
 *    forget to tick.
 *  - **intake** — takes deliveries in and keeps the shelf.
 *  - **engineer** — defines what things are (items, categories, BOMs,
 *    procedures), plans and releases builds, and schedules technicians and
 *    signs off their hours. Gets no clock and no receiving screens unless they
 *    also hold those roles.
 *  - **admin** — hands out roles. Kept apart from engineer on purpose: it is
 *    the one power that cannot be taken back from inside the app.
 *
 * It is still **not a ladder**. Someone who does two jobs holds two roles,
 * visibly, in a row somebody can read.
 *
 * **This file hides controls. It is not the security boundary.** The boundary is
 * the server, in the route. The two must be changed in the same commit.
 */

export type Role = 'admin' | 'engineer' | 'technician' | 'intake'

/** Every role, in the display order used by the roster and the nav. */
export const ROLES: readonly Role[] = ['admin', 'engineer', 'technician', 'intake']

/** One line each, shown next to the checkbox that grants it. */
export const ROLE_DESCRIPTION: Readonly<Record<Role, string>> = {
  admin: 'Decides who holds which role.',
  engineer: 'Defines items, plans builds, and schedules and signs off people’s hours.',
  technician: 'Paid hourly: clocks in, sets availability, builds and maintains robots.',
  intake: 'Receives deliveries and keeps the shelf.',
}

export type Action =
  // Everything a person standing next to a robot does (Operate).
  | 'file_squawk'
  | 'scan'
  | 'file_failure_report'
  // Stock and the bench: receiving, kitting, and moving parts around.
  | 'receive_stock'
  | 'intake_component'
  | 'install_component'
  | 'remove_component'
  | 'record_measurement'
  | 'run_execution'
  | 'open_execution'
  | 'commission_robot'
  | 'print_serials'
  // Buying (§8 step 2).
  | 'manage_purchase_orders'
  // What gets built (§8 steps 1 and 4).
  | 'plan_demand'
  | 'release_build_order'
  | 'assign_roles'
  | 'administer'
  // Desktop work, and the decisions that must not be made in a hurry.
  | 'disposition_failure'
  | 'edit_catalog'
  | 'edit_procedure'
  | 'extend_interval'
  | 'scrap_component'
  | 'override_deviation'
  // Removing a photo is removing evidence (§6), which is why it is its own
  // action rather than a corner of `administer`: it belongs to whoever owns the
  // record of what a part is, not to whoever owns the settings screen.
  | 'delete_attachment'
  // Everyone's hours, and fixing somebody else's.
  | 'view_hours'
  // Asking a technician to come in, and reading everyone's availability.
  | 'schedule_people'
  // Your own hourly life: the clock, your availability, answering requests.
  | 'keep_hours'

/** Anyone signed in can scan a label and write down that something is wrong.
 *  Making either of those a privilege is how they stop happening. */
const EVERYONE: readonly Action[] = ['scan', 'file_squawk', 'file_failure_report']

/** Building and maintaining run the same engine on the same tree (§4.4). */
const EXECUTION: readonly Action[] = [
  'install_component',
  'remove_component',
  'record_measurement',
  'run_execution',
  'open_execution',
  'print_serials',
]

/**
 * What each role may do, with `EVERYONE` folded into every set rather than
 * added by `can`.
 *
 * That is deliberate: it makes `can` a plain union — *you may do X if any role
 * you hold may do X* — and it makes "no roles" mean "nothing", which is the
 * honest reading of a row the database should never have allowed to exist.
 */
const BY_ROLE: Readonly<Record<Role, ReadonlySet<Action>>> = {
  // The smallest set: the only role that can hand out roles, so every power in
  // it is one nobody else can take back.
  admin: new Set<Action>([...EVERYONE, 'assign_roles', 'administer']),
  // What a thing is, what gets built, and who comes in to build it.
  engineer: new Set<Action>([
    ...EVERYONE,
    'edit_catalog',
    'edit_procedure',
    'extend_interval',
    'disposition_failure',
    'scrap_component',
    'override_deviation',
    'delete_attachment',
    'print_serials',
    'plan_demand',
    'release_build_order',
    'commission_robot',
    'manage_purchase_orders',
    'view_hours',
    'schedule_people',
  ]),
  // The floor. Puts parts *into* things; does not take deliveries in unless
  // they also hold intake.
  technician: new Set<Action>([...EVERYONE, ...EXECUTION, 'commission_robot', 'keep_hours']),
  // Receiving is where a part enters the system: the label goes on at the
  // bench where the box was opened, and the incoming check is written down by
  // whoever opened it.
  intake: new Set<Action>([...EVERYONE, 'receive_stock', 'intake_component', 'record_measurement', 'print_serials']),
}

/** May this person do this? The union over the roles they hold. */
export function can(roles: readonly Role[], action: Action): boolean {
  return roles.some((role) => BY_ROLE[role]?.has(action) === true)
}

/**
 * The navigation: **what you see depends on what you do.**
 *
 * A technician's Kronos is their day, their schedule, their hours and their
 * work; an intake person's is deliveries and the shelf; an engineer's is the
 * fleet, builds, items and people. Everyone gets Today. A place a role does not
 * use is not in its nav at all — not greyed out, not "ask a manager".
 *
 * **Scan is not a place.** Nobody's job is "scanning"; it is a step inside
 * work, receiving or looking up a robot, so it is a button on those screens
 * (and a phone's own camera opens any label as a link anyway). As a tab it was
 * a camera page a technician had no reason to visit.
 *
 * **A screen that does not work is not in the navigation.** Squawks, failure
 * reports, purchase orders and reports come back as sections when they exist.
 *
 * Here rather than in the app because it is the same statement as `can`: a
 * place whose actions are all forbidden should not be in the nav.
 */
export type Hub =
  | 'today'
  | 'schedule'
  | 'hours'
  | 'work'
  | 'receive'
  | 'inventory'
  | 'fleet'
  | 'build'
  | 'design'
  | 'people'

/** The tabs inside a place. Only places with more than one job have them. */
export type Section = 'orders' | 'to-buy' | 'schedule' | 'hours' | 'team'

export const SECTIONS_OF: Readonly<Record<Hub, readonly Section[]>> = {
  today: [],
  schedule: [],
  hours: [],
  work: [],
  receive: [],
  inventory: [],
  fleet: [],
  build: ['orders', 'to-buy'],
  design: [],
  people: ['schedule', 'hours', 'team'],
}

/** One order, whatever roles are held, so gaining a role adds places without moving any. */
const HUB_ORDER: readonly Hub[] = [
  'today',
  'schedule',
  'hours',
  'work',
  'receive',
  'inventory',
  'fleet',
  'build',
  'design',
  'people',
]

const HUBS_BY_ROLE: Readonly<Record<Role, readonly Hub[]>> = {
  admin: ['today', 'people'],
  engineer: ['today', 'inventory', 'fleet', 'build', 'design', 'people'],
  // Exactly the phone bar: Today · Schedule · Hours · Work, then More.
  technician: ['today', 'schedule', 'hours', 'work', 'fleet'],
  intake: ['today', 'receive', 'inventory'],
}

/** Which tabs of a shared place each role works in. */
const SECTIONS_BY_ROLE: Readonly<Record<Role, readonly Section[]>> = {
  admin: ['team'],
  engineer: ['orders', 'to-buy', 'schedule', 'hours'],
  technician: [],
  intake: [],
}

export function hubsFor(roles: readonly Role[]): readonly Hub[] {
  const held = new Set<Hub>()
  for (const role of roles) for (const hub of HUBS_BY_ROLE[role] ?? []) held.add(hub)
  return HUB_ORDER.filter((hub) => held.has(hub))
}

/** A place's tabs for these roles, in the place's own order. */
export function sectionsFor(roles: readonly Role[], hub: Hub): readonly Section[] {
  const held = new Set<Section>()
  for (const role of roles) for (const section of SECTIONS_BY_ROLE[role] ?? []) held.add(section)
  return SECTIONS_OF[hub].filter((section) => held.has(section))
}
